import { Link, useParams } from "react-router-dom";
import EmptyState from "@/components/base/EmptyState";
import { newsList } from "@/mocks/news";
import { getNewsById, getTitleById } from "@/utils/content";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getNewsById(id) : undefined;

  useDocumentMeta(
    article ? `${article.title} - 午夜影院影视资讯` : "文章不存在或已删除 - 午夜影院",
    article?.excerpt
  );

  if (!article) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-20 md:px-6">
        <EmptyState
          icon="ri-article-line"
          title="文章不存在或已删除"
          description="这篇资讯可能已经被移除。你可以回到资讯列表，看看最近的影视动态。"
          actionLabel="返回资讯列表"
          actionTo="/news"
        />
      </div>
    );
  }

  const related = newsList.filter((item) => item.id !== article.id).slice(0, 3);
  const relatedTitles = article.relatedTitleIds
    .map((titleId) => getTitleById(titleId))
    .filter((item): item is NonNullable<typeof item> => Boolean(item));

  return (
    <div className="w-full bg-background-50">
      <div className="mx-auto w-full max-w-[860px] px-4 py-8 md:px-6 md:py-12">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-400">
          <Link to="/" className="transition-colors duration-300 hover:text-accent-700">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/news" className="transition-colors duration-300 hover:text-accent-700">
            影视资讯
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="line-clamp-1 text-foreground-600">{article.category}</span>
        </nav>

        <article className="mt-6">
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            <span className="rounded-sm bg-accent-100 px-2 py-1 text-accent-900">{article.category}</span>
            <span className="text-foreground-400">{article.date}</span>
            <span className="text-foreground-300">·</span>
            <span className="text-foreground-400">{article.author}</span>
          </div>

          <h1 className="mt-3 font-heading text-2xl font-semibold leading-snug tracking-tight text-foreground-950 md:text-[32px]">
            {article.title}
          </h1>

          <p className="mt-4 border-l-2 border-accent-400 pl-4 text-sm leading-7 text-foreground-600">
            {article.excerpt}
          </p>

          <div className="mt-6 w-full overflow-hidden rounded-lg bg-background-200">
            <div className="aspect-[16/9] w-full">
              <img
                src={article.cover}
                alt={`${article.title}配图`}
                title={`${article.title} 午夜影院影视资讯`}
                className="h-full w-full object-cover object-center"
              />
            </div>
          </div>

          <div className="article-body mt-8">
            {article.blocks.map((block, index) => {
              if (block.type === "h3") {
                return (
                  <h3 key={index} className="flex items-center gap-2">
                    <span className="h-4 w-[3px] rounded-full bg-accent-500" />
                    {block.text}
                  </h3>
                );
              }
              if (block.type === "img") {
                return (
                  <figure key={index} className="my-7">
                    <div className="w-full overflow-hidden rounded-lg bg-background-200">
                      <div className="aspect-[16/9] w-full">
                        <img
                          src={block.src}
                          alt={block.caption || "资讯配图"}
                          title={`${article.title} 配图`}
                          className="h-full w-full object-cover object-center"
                        />
                      </div>
                    </div>
                    {block.caption ? (
                      <figcaption className="mt-2 text-center text-[11px] text-foreground-400">
                        {block.caption}
                      </figcaption>
                    ) : null}
                  </figure>
                );
              }
              return <p key={index}>{block.text}</p>;
            })}
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-background-200 pt-5">
            <p className="text-[11px] text-foreground-400">
              本文为站内编辑示例内容，仅用于展示资讯阅读体验。
            </p>
            <Link
              to="/news"
              className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-xs text-foreground-700 transition-colors duration-300 hover:border-accent-400 hover:text-accent-700"
            >
              <i className="ri-arrow-left-line" />
              返回资讯列表
            </Link>
          </div>
        </article>

        {relatedTitles.length > 0 ? (
          <section className="mt-10 rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="font-heading text-sm font-semibold text-foreground-950">文中提到的作品</h2>
            <ul className="mt-3 divide-y divide-background-200">
              {relatedTitles.map((item) => (
                <li key={item.id}>
                  <Link
                    to={`/detail/${item.id}`}
                    className="group flex items-center justify-between gap-3 py-2.5 text-xs text-foreground-700 transition-colors duration-300 hover:text-accent-700"
                  >
                    <span className="flex items-center gap-2">
                      <i className="ri-film-line text-foreground-400" />
                      {item.title}
                      <span className="text-foreground-400">
                        {item.year} · {item.rating.toFixed(1)} 分
                      </span>
                    </span>
                    <i className="ri-arrow-right-up-line shrink-0 text-foreground-300" />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <section className="mt-10">
          <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
            <span className="h-4 w-[3px] rounded-full bg-accent-500" />
            相关资讯
          </h2>
          <ul className="mt-4 divide-y divide-background-200 border-y border-background-200">
            {related.map((item) => (
              <li key={item.id}>
                <Link
                  to={`/news/detail/${item.id}`}
                  className="group flex items-center justify-between gap-4 py-4"
                >
                  <span className="min-w-0">
                    <span className="line-clamp-1 text-sm text-foreground-800 transition-colors duration-300 group-hover:text-accent-700">
                      {item.title}
                    </span>
                    <span className="mt-1 block text-[11px] text-foreground-400">
                      {item.category} · {item.date}
                    </span>
                  </span>
                  <i className="ri-arrow-right-up-line shrink-0 text-foreground-300 transition-colors duration-300 group-hover:text-accent-600" />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}